import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { ToastService } from '@shared/services';
import { PermissionService } from './permission.service';
import { TokenService } from './token.service';
import { ForceAny } from '../../shared/typescript';

enum TOAST_CONFIRMATION {
  DELETE = 'Delete',
  COPY = 'Copy',
  EDIT = 'Edit',
  SEND = 'Send',
  AUTO_REJECT = 'Auto-rejected',
  OPERATION_SUCCESS = 'Operation_Success',
}

enum SESSION_KEYS {
  USER_SESSION = 'userSession',
}
const AUTH_ROUTES = [
  'auth',
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
];
/**
 * Service responsible for managing user session timeout and cross-tab synchronization.
 * Handles session expiration warnings, automatic logout, and session extension.
 *
 * @remarks
 * - Uses localStorage for cross-tab session synchronization
 * - Maintains two timers: one for session expiration warning and one for actual logout
 * - Automatically clears session data and tokens on expiration or manual logout
 *
 * @example
 * // Set session after successful login
 * this.sessionService.setSession();
 *
 * // Clear session on logout
 * this.sessionService.clearSession();
 */
@Injectable({
  providedIn: 'root',
})
export class SessionService {
  /**
   * Flag indicating if session timeout occurred
   */
  isSessionTimeout = false;

  /**
   * Flag indicating if user rejected session extension
   */
  isRejected?: boolean;

  readonly SESSION_KEY = SESSION_KEYS.USER_SESSION;
  readonly SESSION_TIMEOUT = 60 * 60 * 1000; // 1 hour
  readonly WARNING_TIME = 15 * 60 * 1000; // 15 minute before session expiration
  private warningTimeoutId!: ForceAny;

  private readonly permissionService = inject(PermissionService);
  private readonly tokenService = inject(TokenService);
  private readonly toastService = inject(ToastService);
  private readonly router = inject(Router);

  /**
   * Creates an instance of SessionService
   * @remarks
   * Sets up storage event listener for cross-tab synchronization
   */
  constructor() {
    // Listen for session changes in other tabs
    window.addEventListener('storage', (event) => {
      if (event.key === this.SESSION_KEY) {
        if (event.newValue) {
          // Session updated in another tab
          if (!this.isAuthRoute()) {
            this.toastService.$isToastVisible.next(false);
            this.clearSession();
          }
        }
      }
    });
  }

  /**
   * Initializes or updates the user session
   * @remarks
   * Stores session expiration time in localStorage and starts timers
   */
  setSession() {
    const expiredOn = Date.now() + this.SESSION_TIMEOUT;
    const sessionInfo = { expiry: expiredOn };
    localStorage.setItem(this.SESSION_KEY, JSON.stringify(sessionInfo));
    this.startSessionTimers();
  }

  /**
   * Clears current session data
   * @remarks
   * Removes session from localStorage and clears active timers
   */
  clearSession(isAutoSessionExpired = false) {
    localStorage.removeItem(this.SESSION_KEY);
    clearTimeout(this.warningTimeoutId);
    if (isAutoSessionExpired) this.isSessionTimeout = true;
  }

  /**
   * Initializes session timers based on remaining session time
   * @private
   * @remarks
   * - Calculates remaining time from localStorage
   * - Sets warning and expiration timers
   * - Automatically handles expired sessions
   */
  private startSessionTimers() {
    clearTimeout(this.warningTimeoutId);

    const remainingTime = this.getRemainingTime();
    if (remainingTime === undefined) return;

    if (remainingTime <= 0) {
      this.handleSessionExpiration();
      return;
    }

    // Set warning timer based on actual remaining time
    const warningTriggerTime = remainingTime - this.WARNING_TIME;
    if (warningTriggerTime > 0) {
      this.warningTimeoutId = setTimeout(
        () => this.showSessionWarning(),
        warningTriggerTime,
      );
    }
  }

  private getRemainingTime() {
    const sessionData = localStorage.getItem(this.SESSION_KEY);
    if (!sessionData) return;

    const sessionInfo = JSON.parse(sessionData);
    return sessionInfo.expiry - Date.now();
  }

  /**
   * Handles session expiration logic
   * @private
   * @remarks
   * Clears session and initiates logout if not already handled
   */
  private handleSessionExpiration() {
    if (!this.isSessionTimeout && !this.isRejected) {
      this.clearSession();
      this.tokenService.logoutCred();
    }
  }

  /**
   * Checks if current route is an authentication-related route
   */
  private isAuthRoute(): boolean {
    const currentPath = this.router.url.split('?')[0]; // Ignore query params
    return AUTH_ROUTES.some((route) => currentPath.includes(route));
  }

  /**
   * Displays session expiration warning toast
   * @private
   * @remarks
   * Provides user with options to extend session or logout
   */
  private showSessionWarning() {
    if (this.isAuthRoute()) return;

    // only this will be trigger in logged in state
    this.toastService.showToast({
      summary: `Session Expiration Warning`,
      detail: 'Your session is about to expire due to inactivity.',
      severity: 'info',
      sticky: true,
      labels: {
        confirm: 'Stay Logged In',
        reject: 'Logout',
      },
      autoClose: {
        duration: this.WARNING_TIME,
        message: 'Time remaining: ',
      },
      onConfirm: () => {
        this.isSessionTimeout = false;
        this.permissionService.getUserProfile().subscribe(() => {
          this.setSession();
          this.toastService.quickToast(
            'Session extended for 1 hour.',
            'success',
          );
        });
      },
      onReject: (msg: string) => {
        this.isRejected = true;
        this.clearSession();
        this.isSessionTimeout = msg === TOAST_CONFIRMATION.AUTO_REJECT;
        this.tokenService.logoutCred();
      },
    });
  }
}
